import { AccessibleClickDirective } from '../../directives/accessible-click.directive';
import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe } from '../../pipes/translate/translate.pipe';
import { IcChevronLeftComponent } from '../Icons/ic-chevron-left/ic-chevron-left';
import { IcPlaneComponent } from '../Icons/ic-plane/ic-plane';
import { IcSparklesComponent } from '../Icons/ic-sparkles/ic-sparkles';
import { IcDeclarationTemplate } from '../Icons/settings-icon/ic-declaration-template/ic-declaration-template';
import { IcManiestTemplate } from '../Icons/settings-icon/ic-manifest-template/ic-manifest-template';
import { SharedPopoverComponent } from '../popover/popover.component';

export type TestStatus = 'idle' | 'running' | 'passed' | 'failed';

export interface TestSummary {
  total: number;
  passed: number;
  failed: number;
  skipped: number;
  duration: number;
}

const RUNNER_URL = 'http://127.0.0.1:4001';

export interface TestSuite {
  id: string;
  title: string;
  description: string;
  iconType: 'plane' | 'manifest-template' | 'templates' | 'default';
  scenarios: string[];
}

const TEST_SUITES: TestSuite[] = [
  {
    id: 'login',
    title: 'loginFlowTests',
    description: 'loginFlowTestsDesc',
    iconType: 'default',
    scenarios: [
      'Page loads – form is visible',
      'Empty form → validation error',
      'Username only → validation error',
      'Password only → validation error',
      'Password < 8 chars → validation error',
      'Enter key on empty form → validation error',
      'Wrong credentials (mocked) → error toast',
      'Forgot Password link → navigates to /auth/forgot-password',
      'Valid credentials (mocked) → navigates to dashboard',
      'Keyboard-only form navigation',
      'Already-authenticated user redirect',
    ],
  },
  {
    id: 'shipments',
    title: 'shipmentListTests',
    description: 'shipmentListTestsDesc',
    iconType: 'plane',
    scenarios: [
      'Filter – Global Search',
      'Sort – Column Header',
      'Resize – Column Width',
      'Reorder – Drag & Drop',
      'Preferences – Save Settings',
      'Column Filters – Per Column',
    ],
  },
  {
    id: 'declarations',
    title: 'declarationListTests',
    description: 'declarationListTestsDesc',
    iconType: 'default',
    scenarios: [
      'Filter – Global Search',
      'Sort – Column Header',
      'Resize – Column Width',
      'Reorder – Drag & Drop',
      'Preferences – Save Settings',
      'Column Filters – Per Column',
    ],
  },
  {
    id: 'brokers',
    title: 'brokersTests',
    description: 'brokersTestsDesc',
    iconType: 'default',
    scenarios: [
      'Filter – Search',
      'Sort – Headers',
      'Resize – Columns',
      'Save Settings',
      'Column Filters – Per Column',
    ],
  },
  {
    id: 'clients',
    title: 'clientsTests',
    description: 'clientsTestsDesc',
    iconType: 'default',
    scenarios: [
      'Filter – Search',
      'Sort – Headers',
      'Resize – Columns',
      'Save Settings',
      'Column Filters – Per Column',
    ],
  },
  {
    id: 'users',
    title: 'usersTests',
    description: 'usersTestsDesc',
    iconType: 'default',
    scenarios: [
      'Filter – Search',
      'Sort – Headers',
      'Resize – Columns',
      'Save Settings',
      'Column Filters – Per Column',
    ],
  },
  {
    id: 'roles',
    title: 'rolesTests',
    description: 'rolesTestsDesc',
    iconType: 'default',
    scenarios: [
      'Filter – Search',
      'Sort – Headers',
      'Resize – Columns',
      'Save Settings',
      'Column Filters – Per Column',
    ],
  },
  {
    id: 'manifest-templates',
    title: 'manifestTemplatesTests',
    description: 'manifestTemplatesTestsDesc',
    iconType: 'manifest-template',
    scenarios: [
      'Manifest – Search',
      'Manifest – Sort',
      'Manifest – Create',
      'Manifest – Edit',
    ],
  },
  {
    id: 'parties',
    title: 'partiesTests',
    description: 'partiesTestsDesc',
    iconType: 'default',
    scenarios: [
      'Filter – Search',
      'Sort – Headers',
      'Resize – Columns',
      'Save Settings',
      'Column Filters – Per Column',
    ],
  },
  {
    id: 'nested-items',
    title: 'itemsTests',
    description: 'itemsTestsDesc',
    iconType: 'default',
    scenarios: [
      'Decl Items – Search',
      'Decl Items – Sort',
      'Deliv Items – Search',
      'Deliv Items – Sort',
    ],
  },
  {
    id: 'shipment-dialog',
    title: 'shipmentDialogTests',
    description: 'shipmentDialogTestsDesc',
    iconType: 'plane',
    scenarios: [
      'Create – Dialog Fields',
      'Create – Empty Submit → Error',
      'Create – Manifest Upload',
      'Create – ShipmentNo + File → Warning',
      'Edit – status=new · Client disabled',
      'Edit – clearanceInProgress · config enabled',
      'Edit – completed · form fully disabled',
      'Edit – Save Changes',
    ],
  },
  {
    id: 'broker-client-flow',
    title: 'brokerClientFlowTests',
    description: 'brokerClientFlowTestsDesc',
    iconType: 'plane',
    scenarios: [
      'Broker – Form disabled when empty',
      'Broker – Invalid field pattern errors',
      'Broker – Create (mocked)',
      'Client – Form disabled when empty',
      'Client – Create with H1/H7 + broker link',
      'Decl Group – Create (Mark as default)',
      'H1 Template – Create inside group',
      'H7 Template – Create inside group',
      'Client Config – Assign manifest template',
      'Shipment – Create with client from flow',
    ],
  },
  {
    id: 'user-flow',
    title: 'userFlowTests',
    description: 'userFlowTestsDesc',
    iconType: 'default',
    scenarios: [
      'Broker – Create successfully',
      'Client – Create for broker',
      'Role – Create role for client',
      'User – Create client user',
    ],
  },
  {
    id: 'delivery-items',
    title: 'deliveryItemsTests',
    description: 'deliveryItemsTestsDesc',
    iconType: 'default',
    scenarios: [
      'Create – Form & Mock',
      'Inline Edit – Double Click',
      'Inline Edit – Action Icon',
      'Clone – Menu Action',
      'Edit – Menu Action',
      'Party Conflict – Consignee Update',
    ],
  },
  {
    id: 'transit-declarations',
    title: 'transitDeclarationsTests',
    description: 'transitDeclarationsTestsDesc',
    iconType: 'default',
    scenarios: [
      'Filter – Search & Tabs',
      'Create – Tabbed Form & Submit',
      'Details – Edit Incidents',
    ],
  },
];

/**
 * E2eTestRunnerComponent component.
 * Provides UI presentation and event handling for e2e test runner component.
 */
@Component({
  selector: 'app-e2e-test-runner',
  standalone: true,
  imports: [
    AccessibleClickDirective,
    CommonModule,
    MatTooltipModule,
    TranslatePipe,
    SharedPopoverComponent,
    IcDeclarationTemplate,
    IcManiestTemplate,
    IcPlaneComponent,
    IcSparklesComponent,
    IcChevronLeftComponent,
  ],
  templateUrl: './e2e-test-runner.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './e2e-test-runner.component.scss',
})
export class E2eTestRunnerComponent implements OnInit, OnDestroy {
  /** Property current view. */
  currentView = signal<'menu' | 'runner'>('menu');
  /** Property selected suite. */
  selectedSuite = signal<TestSuite | null>(null);

  /** Property status. */
  status = signal<TestStatus>('idle');
  /** Property summary. */
  summary = signal<TestSummary | null>(null);
  /** Property last run at. */
  lastRunAt = signal<string | null>(null);
  /** Property error message. */
  errorMessage = signal<string | null>(null);
  /** Reactive signal holding server available. */
  serverAvailable = signal(false);
  /** Property live log. */
  liveLog = signal<string[]>([]);
  /** Property current test. */
  currentTest = signal<string | null>(null);

  /** Property test suites. */
  readonly testSuites = TEST_SUITES;

  /**
   * Reference to the panel ref element or component.
   */
  @ViewChild('panelRef') panelRef!: SharedPopoverComponent;
  /**
   * Reference to the log container element or component.
   */
  @ViewChild('logContainer') logContainer?: ElementRef<HTMLElement>;

  /** Property poll interval. */
  private pollInterval: ReturnType<typeof setInterval> | null = null;
  /** Property event source. */
  private eventSource: EventSource | null = null;
  /** Property session started. */
  private sessionStarted = false;

  /**
   * Lifecycle hook that runs on component initialization.
   */
  ngOnInit(): void {
    // Intentionally empty – do not automatically ping the test runner server on page load
  }

  /**
   * Lifecycle hook that cleans up subscriptions on component destruction.
   */
  ngOnDestroy(): void {
    this.stopPolling();
    this.closeEventSource();
  }

  /**
   * Handles on trigger click logic.
   */
  onTriggerClick(): void {
    this.checkServerAvailability();
  }

  /**
   * Handles select suite logic.
   * @param suite - The suite
   */
  selectSuite(suite: TestSuite): void {
    this.selectedSuite.set(suite);
    this.currentView.set('runner');
    // Stop any ongoing poll/stream from a previous suite before resetting state
    this.stopPolling();
    this.closeEventSource();
    // Reset stale results from a different suite's run
    this.summary.set(null);
    this.liveLog.set([]);
    this.currentTest.set(null);
    this.status.set('idle');
    this.sessionStarted = false; // Don't re-apply summary from a previous suite's run
    this.fetchStatus(); // Get the latest status for this specific suite
  }

  /**
   * Handles back to menu logic.
   */
  backToMenu(): void {
    this.currentView.set('menu');
    this.selectedSuite.set(null);
  }

  /**
   * Handles run tests logic.
   */
  async runTests(): Promise<void> {
    const suite = this.selectedSuite();
    if (!suite) return;

    this.errorMessage.set(null);
    this.liveLog.set([]);
    this.currentTest.set(null);
    this.sessionStarted = true;
    try {
      const res = await fetch(`${RUNNER_URL}/run-tests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ suiteId: suite.id }),
      });

      if (res.status === 409) {
        // already running – subscribe to stream anyway
      } else if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        this.errorMessage.set(
          (body as any)?.message ?? 'Failed to start tests'
        );
        return;
      }
      this.status.set('running');
      this.subscribeToStream();
    } catch {
      this.errorMessage.set(
        'Test runner server is not reachable.\nStart it with: node scripts/e2e-runner-server.mjs'
      );
    }
  }

  /**
   * Handles open report logic.
   */
  openReport(): void {
    window.open(`${RUNNER_URL}/test-report`, '_blank');
  }

  /**
   * Handles subscribe to stream logic.
   */
  private subscribeToStream(): void {
    this.closeEventSource();
    this.eventSource = new EventSource(`${RUNNER_URL}/test-stream`);

    this.eventSource.onmessage = (e) => {
      try {
        const line: string = JSON.parse(e.data);
        this.liveLog.update((lines) => [...lines, line]);
        this.extractCurrentTest(line);
        this.scrollLogToBottom();
      } catch {
        /* ignore parse errors */
      }
    };

    this.eventSource.addEventListener('done', (e: MessageEvent) => {
      try {
        const data = JSON.parse(e.data);
        // Only apply results that belong to the suite we triggered
        const selectedId = this.selectedSuite()?.id ?? null;
        const suiteMatches =
          data.suiteId != null && data.suiteId === selectedId;
        this.status.set(data.status ?? 'idle');
        if (suiteMatches) {
          this.summary.set(data.summary ?? null);
          this.lastRunAt.set(data.lastRunAt ?? null);
        }
        this.currentTest.set(null);
      } catch {
        /* ignore */
      }
      this.closeEventSource();
    });

    this.eventSource.onerror = () => {
      // Connection closed (server done or unreachable) – fall back to polling
      this.closeEventSource();
      this.startPolling();
    };
  }

  /**
   * Handles extract current test logic.
   * @param line - The line
   */
  private extractCurrentTest(line: string): void {
    const match = line.match(/[›>]\s+(.+?)(?:\s*\(\d+ms\))?$/);
    if (match) this.currentTest.set(match[1].trim());
  }

  /**
   * Handles scroll log to bottom logic.
   */
  private scrollLogToBottom(): void {
    setTimeout(() => {
      if (this.logContainer?.nativeElement) {
        this.logContainer.nativeElement.scrollTop =
          this.logContainer.nativeElement.scrollHeight;
      }
    }, 0);
  }

  /**
   * Handles close event source logic.
   */
  private closeEventSource(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
  }

  /**
   * Handles check server availability logic.
   */
  private async checkServerAvailability(): Promise<void> {
    try {
      const res = await fetch(`${RUNNER_URL}/test-status`, {
        signal: AbortSignal.timeout(2_000),
      });
      if (res.ok) {
        this.serverAvailable.set(true);
        const data: any = await res.json();
        this.applyStatusData(data);
        // Re-subscribe if already running
        if (data.status === 'running') this.subscribeToStream();
      }
    } catch {
      this.serverAvailable.set(false);
    }
  }

  /**
   * Handles fetch status logic.
   */
  private async fetchStatus(): Promise<void> {
    const suite = this.selectedSuite();
    const url = suite
      ? `${RUNNER_URL}/test-status?suiteId=${suite.id}`
      : `${RUNNER_URL}/test-status`;

    try {
      const res = await fetch(url);
      if (!res.ok) return;
      const data: any = await res.json();
      this.serverAvailable.set(true);
      this.applyStatusData(data);
    } catch {
      this.serverAvailable.set(false);
    }
  }

  /**
   * Handles apply status data logic.
   * @param data - The data
   */
  private applyStatusData(data: any): void {
    const selectedId = this.selectedSuite()?.id ?? null;
    // Only show a completed summary if it belongs to the currently selected suite.
    // A null suiteId on the server means an old run before tracking was added –
    // treat it as a mismatch to avoid showing stale all-tests results.
    const suiteMatches = data.suiteId != null && data.suiteId === selectedId;

    if (data.status === 'running') {
      this.sessionStarted = true; // Adopt running sessions
      this.status.set('running');
      this.startPolling();
    } else if (this.sessionStarted && suiteMatches) {
      this.status.set(data.status ?? 'idle');
      this.summary.set(data.summary ?? null);
      this.lastRunAt.set(data.lastRunAt ?? null);
      this.stopPolling();
    } else {
      // Clear results if session hasn't started yet or suite doesn't match
      this.status.set('idle');
      this.summary.set(null);
      this.lastRunAt.set(null);
      this.stopPolling();
    }
  }

  /**
   * Handles start polling logic.
   */
  private startPolling(): void {
    if (this.pollInterval) return;
    this.pollInterval = setInterval(() => this.fetchStatus(), 2_000);
  }

  /**
   * Handles stop polling logic.
   */
  private stopPolling(): void {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }
}
